<script setup lang="ts">
import { computed } from 'vue'
import { useConsumablesStore, type Consumable } from '../stores/consumables'

const props = defineProps<{
  consumable: Consumable
  canManage?: boolean
  canUse?: boolean
}>()

const emit = defineEmits<{
  (e: 'use'): void
  (e: 'restock'): void
  (e: 'edit'): void
  (e: 'click'): void
}>()

const store = useConsumablesStore()
const API_BASE = import.meta.env.VITE_API_URL || ''

const stockLevel = computed(() => store.getStockLevel(props.consumable))
const stockColor = computed(() => store.getStockColor(stockLevel.value))
const stockPercentage = computed(() =>
  Math.round((props.consumable.current_quantity / props.consumable.max_quantity) * 100)
)

function formatQuantity(qty: number, unit: string | null): string {
  if (!unit) return qty.toString()
  return `${qty} ${unit}`
}
</script>

<template>
  <div
    class="card hover:shadow-lg transition-shadow cursor-pointer"
    @click="$emit('click')"
  >
    <!-- Header with photo thumbnail -->
    <div class="flex gap-3 mb-3">
      <!-- Thumbnail -->
      <div class="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
        <img
          v-if="consumable.photo_url"
          :src="`${API_BASE}${consumable.photo_url}`"
          :alt="consumable.name"
          class="w-full h-full object-cover"
        />
        <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      </div>

      <!-- Name and status -->
      <div class="flex-1 min-w-0">
        <h3 class="font-semibold text-gray-800 truncate">{{ consumable.name }}</h3>
        <p v-if="consumable.location_name" class="text-sm text-gray-500">
          {{ consumable.location_name }}
        </p>
        <span
          :class="[
            'inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium',
            stockLevel === 'empty' ? 'bg-red-100 text-red-800' :
            stockLevel === 'low' ? 'bg-orange-100 text-orange-800' :
            stockLevel === 'partial' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          ]"
        >
          {{ stockLevel === 'empty' ? 'Empty' :
             stockLevel === 'low' ? 'Low Stock' :
             stockLevel === 'partial' ? 'Available' :
             'Well Stocked' }}
        </span>
      </div>
    </div>

    <!-- Quantity Progress Bar -->
    <div class="mb-3">
      <div class="flex justify-between text-sm mb-1">
        <span class="text-gray-600">
          {{ formatQuantity(consumable.current_quantity, consumable.unit) }}
        </span>
        <span class="text-gray-400">
          / {{ formatQuantity(consumable.max_quantity, consumable.unit) }}
        </span>
      </div>
      <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          :class="['h-full transition-all', stockColor]"
          :style="{ width: `${stockPercentage}%` }"
        ></div>
      </div>
    </div>

    <!-- Description -->
    <p v-if="consumable.description" class="text-sm text-gray-600 mb-3 line-clamp-2">
      {{ consumable.description }}
    </p>

    <!-- Actions -->
    <div
      class="flex gap-2 pt-3 border-t"
      @click.stop
    >
      <button
        v-if="canUse && stockLevel !== 'empty'"
        @click="$emit('use')"
        class="btn btn-sm flex-1"
      >
        Used Some
      </button>
      <button
        v-if="canManage"
        @click="$emit('restock')"
        class="btn btn-sm btn-primary flex-1"
      >
        Restock
      </button>
      <button
        v-if="canManage"
        @click="$emit('edit')"
        class="btn btn-sm"
        title="Edit"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      </button>
    </div>

    <!-- Last restocked info -->
    <p
      v-if="consumable.last_restocked_at"
      class="text-xs text-gray-400 mt-2 text-right"
    >
      Last restocked {{ new Date(consumable.last_restocked_at).toLocaleDateString() }}
      <span v-if="consumable.restocked_by_name">by {{ consumable.restocked_by_name }}</span>
    </p>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
